// RenewalReviewPage — landing rendered at /revision/:token. NO auth.
//
// At renewal time the agent sends the client this encrypted link. The client
// sees ONLY the personal-data fields the agent chose, then confirms
// "Todo correcto" or "Necesito un ajuste" + a comment. The agent sees the
// answer back in the CRM. Data fetched via /portal/review/:token (token IS
// the auth — no JWT, no MSAL). Mirrors ClientPortalPage's public-page style.

import React, { useEffect, useState, useCallback } from "react";
import {
  ShieldCheck, CheckCircle, Pencil, Loader2, AlertCircle, Phone, Mail, HelpCircle, BookOpen,
} from "lucide-react";
import { getRenewalReview, respondRenewalReview } from "../api.js";
import { preguntasVisibles, responderPregunta, faltanObligatorias, LARGO_MAXIMO_TEXTO } from "../preguntasDeRevision.js";
import {
  PASOS_ID_ME, CONSEJOS_ID_ME, AVISO_DE_AYUDA, ENLACE_OFICIAL,
  LARGO_MAXIMO_CORREO, correoValido, enIdioma, laminaDeIdMe,
} from "../preguntasDeIdMe.js";
import { assetUrl } from "../assetUrl.js";
import { safeHtml } from "../sanitizeHtml.js";
import { CandadoDelSsn, Centered } from "./CandadoDelSsn.jsx";

const BRAND = "#00d4ff";

export function RenewalReviewPage({ token, lang: initialLang }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  // El candado (Lissette 2026-09-25): mientras el servidor diga `locked`, la
  // pagina pide los 4 ultimos del SSN y no enseña
  // nada del cliente. El codigo bueno se queda SOLO en memoria: recargar la
  // pagina lo vuelve a pedir, y contestar lo manda otra vez.
  const [bloqueo, setBloqueo] = useState(null);
  const [codigo, setCodigo] = useState("");
  const [verificando, setVerificando] = useState(false);

  // Selection + comment state.
  const [choice, setChoice] = useState(null); // 'correct' | 'adjust'
  const [comment, setComment] = useState("");
  // Respuestas a las PREGUNTAS del enlace (hoy: si tiene cuenta ID.me y, solo
  // si contesta que no, si quiere que le ayudemos a crearla).
  // { [key]: 'yes' | 'no' } — una pregunta sin tocar no viaja.
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return initialLang || "es";
    const url = new URLSearchParams(window.location.search);
    return url.get("lang") || initialLang || "es";
  });
  const t = (es, en) => (lang === "es" ? es : en);

  // Las preguntas que toca enseñar AHORA. La de "¿necesitas ayuda para crear tu
  // cuenta ID.me?" solo aparece despues de que conteste que no la tiene: a
  // quien ya la tiene no se le pregunta como crearla (Lissette 2026-09-02,
  // punto 72). Ver crm-app/preguntasDeRevision.js.
  const visibles = preguntasVisibles(data && data.questions, answers);
  // Las obligatorias que faltan. Mientras haya alguna no se puede enviar, y la
  // pagina dice cual es en vez de dejar el boton apagado sin explicacion
  // (Lissette 2026-09-09).
  const faltan = faltanObligatorias(data && data.questions, answers);
  // Un correo a medio escribir tampoco se manda: el servidor lo tiraria y el
  // cliente creeria que lo dejo puesto.
  const correoMalEscrito = visibles.some(
    (q) => q.answerKind === "email" && answers[q.key] && !correoValido(answers[q.key]),
  );
  const puedeEnviar = !!choice && !faltan.length && !correoMalEscrito;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const d = await getRenewalReview(token);
      if (d?.locked) {
        setBloqueo(d);
        if (!new URLSearchParams(window.location.search).get("lang") && d.preferredLanguage) {
          setLang(d.preferredLanguage);
        }
        return;
      }
      setData(d);
      if (!new URLSearchParams(window.location.search).get("lang") && d?.client?.preferredLanguage) {
        setLang(d.client.preferredLanguage);
      }
      // Pre-fill if the client already responded (they can update it).
      if (d?.response) {
        setChoice(d.response);
        setComment(d.comment || "");
      }
      if (d?.answers && typeof d.answers === "object") setAnswers(d.answers);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  async function desbloquear(intento) {
    if (verificando) return;
    setVerificando(true);
    try {
      const d = await getRenewalReview(token, intento);
      if (d?.locked) { setBloqueo(d); return; }
      setCodigo(intento);
      setBloqueo(null);
      setData(d);
      if (d?.response) {
        setChoice(d.response);
        setComment(d.comment || "");
      }
      if (d?.answers && typeof d.answers === "object") setAnswers(d.answers);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setVerificando(false);
    }
  }

  async function submit() {
    if (!puedeEnviar || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await respondRenewalReview(token, { response: choice, comment: comment.trim() || null, answers }, codigo);
      setDone(true);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <Centered><Loader2 size={34} className="dkt-spin" color={BRAND} /></Centered>;

  if (bloqueo) {
    return <CandadoDelSsn bloqueo={bloqueo} t={t} verificando={verificando} onProbar={desbloquear} />;
  }

  if (error && !data) {
    return (
      <Centered>
        <AlertCircle size={40} color="#ef4444" />
        <h2 style={{ margin: "1rem 0 .4rem", color: "#0a1628" }}>
          {t("Link no válido", "Invalid link")}
        </h2>
        <p style={{ color: "#5b7a9a", maxWidth: 360, textAlign: "center" }}>
          {t("Este link expiró o ya no está disponible. Pídele a tu agente uno nuevo.",
             "This link expired or is no longer available. Ask your agent for a new one.")}
        </p>
      </Centered>
    );
  }
  if (!data) return null;

  const agentName = data.agent?.displayName || data.agency?.name || "Deskte";

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(180deg, ${BRAND}11 0%, #f4f7fb 280px)`,
      fontFamily: "system-ui,-apple-system,Segoe UI,sans-serif",
      color: "#0a1628",
    }}>
      {/* Header */}
      <header style={{
        background: "#fff", borderBottom: "1px solid #e1e8f0", padding: ".9rem 1.25rem",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {data.agency?.logoUrl
            ? <img src={data.agency.logoUrl} alt="" style={{ maxHeight: 38, maxWidth: 150, objectFit: "contain" }} />
            : <div style={{ fontWeight: 800, fontSize: "1.05rem" }}>{data.agency?.name || "Deskte"}</div>}
          <div style={{ fontSize: ".7rem", color: "#5b7a9a" }}>
            {t("Revisión de renovación", "Renewal review")}
          </div>
        </div>
        <button onClick={() => setLang(lang === "es" ? "en" : "es")} style={{
          padding: ".45rem .85rem", borderRadius: 999, background: "#f4f7fb", color: "#3a5a7a",
          border: "1px solid #e1e8f0", cursor: "pointer", fontWeight: 700, fontSize: ".75rem",
        }}>
          {lang === "es" ? "🇺🇸 EN" : "🇪🇸 ES"}
        </button>
      </header>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "1.25rem" }}>
        {done ? (
          <SuccessCard t={t} choice={choice} agentName={agentName} />
        ) : (
          <>
            {/* Greeting + intro */}
            <h1 style={{ margin: "0 0 .3rem", fontSize: "1.45rem" }}>
              {t("Hola", "Hi")}, {data.client.firstName} 👋
            </h1>
            <p style={{ margin: "0 0 1.25rem", color: "#5b7a9a", fontSize: ".93rem", lineHeight: 1.55 }}>
              {t(
                `${agentName} quiere confirmar que tu información está correcta para tu renovación. Revísala y dinos si está bien o si hay que ajustar algo.`,
                `${agentName} wants to confirm your information is correct for your renewal. Please review it and let us know if it's right or needs a change.`,
              )}
            </p>

            {/* Data card */}
            <div style={{
              background: "#fff", border: "1px solid #e1e8f0", borderRadius: 14,
              overflow: "hidden", marginBottom: "1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,.04)",
            }}>
              <div style={{
                padding: ".75rem 1rem", borderBottom: "1px solid #eef2f7",
                fontSize: ".72rem", fontWeight: 800, letterSpacing: ".06em",
                textTransform: "uppercase", color: "#5b7a9a",
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <ShieldCheck size={15} color={BRAND} />
                {t("Tus datos", "Your information")}
              </div>
              {(data.fields || []).map((f) => (
                <div key={f.key} style={{
                  display: "flex", justifyContent: "space-between", gap: 16,
                  padding: ".7rem 1rem", borderBottom: "1px solid #f3f6fa",
                }}>
                  <span style={{ color: "#5b7a9a", fontSize: ".85rem", flexShrink: 0 }}>
                    {lang === "es" ? f.labelEs : f.labelEn}
                  </span>
                  <span style={{
                    fontWeight: 600, fontSize: ".9rem", textAlign: "right", wordBreak: "break-word",
                    whiteSpace: "pre-line", color: f.value ? "#0a1628" : "#b6c4d4",
                  }}>
                    {f.value || t("— (vacío)", "— (empty)")}
                  </span>
                </div>
              ))}
            </div>

            {/* Preguntas — lo que el CRM no sabe y solo el cliente puede
                contestar (hoy: si tiene cuenta ID.me). Van en su propia
                tarjeta, fuera de "Tus datos": no son algo que confirmar, son
                algo que responder, y una pregunta sin contestar dentro de la
                lista de datos parecía un dato vacío de su ficha. */}
            {visibles.length > 0 && (
              <div style={{
                background: "#fff", border: "1px solid #e1e8f0", borderRadius: 14,
                overflow: "hidden", marginBottom: "1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,.04)",
              }}>
                <div style={{
                  padding: ".75rem 1rem", borderBottom: "1px solid #eef2f7",
                  fontSize: ".72rem", fontWeight: 800, letterSpacing: ".06em",
                  textTransform: "uppercase", color: "#5b7a9a",
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <HelpCircle size={15} color={BRAND} />
                  {visibles.length === 1
                    ? t("Una pregunta más", "One more question")
                    : t("Unas preguntas más", "A few more questions")}
                </div>
                {visibles.map((q) => (
                  <div key={q.key} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    gap: 16, flexWrap: "wrap",
                    padding: ".7rem 1rem", borderBottom: "1px solid #f3f6fa",
                  }}>
                    <span style={{ color: "#0a1628", fontSize: ".88rem", fontWeight: 600 }}>
                      {lang === "es" ? q.labelEs : q.labelEn}
                      {q.required && <span style={{ color: "#dc2626", marginLeft: 4 }} aria-hidden="true">*</span>}
                      {/* El dato de la ficha que hace la pregunta contestable:
                          el correo que tenemos guardado. Sin verlo, "es el
                          mismo?" no se puede responder. Si la ficha no tiene
                          correo se dice, en vez de dejar un hueco mudo. */}
                      {Object.prototype.hasOwnProperty.call(q, "hint") && (
                        <span style={{
                          display: "block", marginTop: 3, fontWeight: 500,
                          fontSize: ".82rem", color: q.hint ? "#2a5a82" : "#94a3b8",
                        }}>
                          {/* Con el correo suelto, el cliente no sabe QUE es
                              esa linea. Decir de donde sale es la diferencia
                              entre poder contestar y adivinar. */}
                          {q.hint
                            ? <>{t("El que tenemos: ", "The one we have: ")}<strong>{q.hint}</strong></>
                            : t("No tenemos ningún correo tuyo guardado.",
                                "We don't have any email on file for you.")}
                        </span>
                      )}
                    </span>
                    {q.answerKind === "texto" ? (
                      /* UNA RESPUESTA ESCRITA (hoy, el motivo de no renovar).
                         Un recuadro y no una casilla de una linea: un motivo se
                         escribe en una o dos frases, y en un telefono una
                         casilla estrecha invita a poner "no" y nada mas — que es
                         exactamente lo que esta pregunta existe para evitar
                         (Lissette 2026-09-15). */
                      <div style={{ flex: "1 1 260px", minWidth: 220 }}>
                        <textarea
                          rows={3}
                          maxLength={LARGO_MAXIMO_TEXTO}
                          value={answers[q.key] || ""}
                          onChange={(e) => setAnswers((a) => responderPregunta(data.questions, a, q.key, e.target.value))}
                          placeholder={t("Cuéntanos por qué, con tus palabras.", "Tell us why, in your own words.")}
                          style={{
                            width: "100%", padding: ".55rem .75rem", borderRadius: 10,
                            border: "1px solid #d8e2ec", fontSize: ".9rem", fontFamily: "inherit",
                            outline: "none", boxSizing: "border-box", resize: "vertical",
                          }}
                        />
                      </div>
                    ) : q.answerKind === "email" ? (
                      <div style={{ flex: "1 1 240px", minWidth: 200 }}>
                        <input
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          maxLength={LARGO_MAXIMO_CORREO}
                          value={answers[q.key] || ""}
                          onChange={(e) => setAnswers((a) => responderPregunta(data.questions, a, q.key, e.target.value))}
                          placeholder={t("tucorreo@ejemplo.com", "youremail@example.com")}
                          style={{
                            width: "100%", padding: ".55rem .75rem", borderRadius: 10,
                            border: `1px solid ${answers[q.key] && !correoValido(answers[q.key]) ? "#fca5a5" : "#d8e2ec"}`,
                            fontSize: ".9rem", fontFamily: "inherit", outline: "none", boxSizing: "border-box",
                          }}
                        />
                        {answers[q.key] && !correoValido(answers[q.key]) && (
                          <div style={{ marginTop: 4, fontSize: ".78rem", color: "#b91c1c" }}>
                            {t("Revisa el correo: parece que falta algo.",
                               "Check the email: something looks missing.")}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 8 }}>
                        <YesNoBtn
                          active={answers[q.key] === "yes"} color="#16a34a"
                          label={t("Sí", "Yes")}
                          onClick={() => setAnswers((a) => responderPregunta(data.questions, a, q.key, "yes"))}
                        />
                        <YesNoBtn
                          active={answers[q.key] === "no"} color="#64748b"
                          label={t("No", "No")}
                          onClick={() => setAnswers((a) => responderPregunta(data.questions, a, q.key, "no"))}
                        />
                      </div>
                    )}
                  </div>
                ))}
                {/* Quien contesta que NO tiene cuenta se queda parado: la
                    pregunta le dice que hace falta y no como se consigue. Aqui
                    van los pasos, dentro de la propia pagina. */}
                {answers.idme === "no" && <GuiaIdMe lang={lang} t={t} />}
              </div>
            )}

            {/* Already-responded note */}
            {data.alreadyResponded && (
              <div style={{
                background: "#eef7ff", border: "1px solid #cfe6fb", borderRadius: 10,
                padding: ".7rem .9rem", marginBottom: "1rem", fontSize: ".82rem", color: "#2a5a82",
              }}>
                {t("Ya enviaste una respuesta. Puedes actualizarla si quieres.",
                   "You already sent a response. You can update it if you want.")}
              </div>
            )}

            {/* Choice buttons */}
            <div style={{ display: "flex", gap: 12, marginBottom: "1rem", flexWrap: "wrap" }}>
              <ChoiceBtn
                active={choice === "correct"} color="#16a34a"
                icon={<CheckCircle size={20} />}
                label={t("Todo correcto", "All correct")}
                onClick={() => setChoice("correct")}
              />
              <ChoiceBtn
                active={choice === "adjust"} color="#ea580c"
                icon={<Pencil size={18} />}
                label={t("Necesito un ajuste", "Needs a change")}
                onClick={() => setChoice("adjust")}
              />
            </div>

            {/* Comment box */}
            <label style={{ display: "block", marginBottom: "1.25rem" }}>
              <span style={{ fontSize: ".82rem", color: "#5b7a9a", fontWeight: 600 }}>
                {choice === "adjust"
                  ? t("¿Qué hay que ajustar?", "What needs to change?")
                  : t("Comentario (opcional)", "Comment (optional)")}
              </span>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder={t("Escribe aquí…", "Type here…")}
                style={{
                  width: "100%", marginTop: 6, padding: ".65rem .8rem", borderRadius: 10,
                  border: "1px solid #d8e2ec", fontSize: ".9rem", fontFamily: "inherit",
                  resize: "vertical", outline: "none", boxSizing: "border-box",
                }}
              />
            </label>

            {error && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
                padding: ".7rem .9rem", marginBottom: "1rem", color: "#b91c1c",
                fontSize: ".85rem", display: "flex", alignItems: "center", gap: 8,
              }}>
                <AlertCircle size={16} /> {error}
              </div>
            )}

            {/* Un boton apagado sin explicacion se lee como "la pagina esta
                rota". Si falta algo, se dice que falta. */}
            {(faltan.length > 0 || correoMalEscrito) && choice && (
              <div style={{
                background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10,
                padding: ".7rem .9rem", marginBottom: "1rem", color: "#92400e", fontSize: ".85rem",
              }}>
                {correoMalEscrito
                  ? t("Revisa el correo que escribiste antes de enviar.",
                       "Check the email you typed before sending.")
                  : t("Antes de enviar, contesta las preguntas marcadas con *.",
                       "Before sending, answer the questions marked with *.")}
              </div>
            )}

            <button
              onClick={submit}
              disabled={!puedeEnviar || submitting}
              style={{
                width: "100%", padding: ".9rem", borderRadius: 12, border: "none",
                background: puedeEnviar ? BRAND : "#cbd5e1", color: "#06283a",
                fontWeight: 800, fontSize: "1rem",
                cursor: puedeEnviar && !submitting ? "pointer" : "not-allowed",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {submitting ? <Loader2 size={18} className="dkt-spin" /> : null}
              {t("Enviar a mi agente", "Send to my agent")}
            </button>

            {/* Pie de contacto del agente ASIGNADO. Solo cuando no hay firma:
                con firma, los mismos teléfonos saldrían dos veces seguidas. */}
            {!data.agentSignatureHtml && (data.agent?.phone || data.agent?.email) && (
              <div style={{ marginTop: "1.5rem", textAlign: "center", fontSize: ".82rem", color: "#5b7a9a" }}>
                {t("¿Dudas? Contacta a", "Questions? Contact")} {agentName}:
                <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 6 }}>
                  {data.agent?.phone && (
                    <a href={`tel:${data.agent.phone}`} style={{ color: BRAND, display: "flex", alignItems: "center", gap: 4, textDecoration: "none" }}>
                      <Phone size={14} /> {data.agent.phone}
                    </a>
                  )}
                  {data.agent?.email && (
                    <a href={`mailto:${data.agent.email}`} style={{ color: BRAND, display: "flex", alignItems: "center", gap: 4, textDecoration: "none" }}>
                      <Mail size={14} /> {data.agent.email}
                    </a>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* LA FIRMA DEL AGENTE, AL FINAL (Lissette 2026-09-09).
            Es la misma que va al final de sus correos, armada en el servidor
            desde Mi Perfil, así que no hay dos firmas que puedan separarse.

            Va FUERA del ternario a propósito: cierra la página tanto antes de
            contestar como en la pantalla de "¡Gracias!". Un cliente que acaba
            de confirmar es justo el que se queda mirando cómo llamar.

            Pasa por `safeHtml` como todo el HTML que el CRM pinta en su propio
            origen. Aquí llega ya escapado del servidor, pero esta página es
            PÚBLICA: es la última que puede permitirse depender de una sola
            capa. `overflowX` porque una línea de contactos larga no puede
            empujar la página entera a lo ancho en un teléfono. */}
        {data.agentSignatureHtml && (
          <div style={{ overflowX: "auto", fontFamily: "system-ui,-apple-system,Segoe UI,sans-serif" }}>
            <div dangerouslySetInnerHTML={safeHtml(data.agentSignatureHtml)} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Los pasos para crear la cuenta ID.me, para quien acaba de decir que no la
 * tiene (Lissette 2026-09-09: *"si dice no, mandar un aviso: vea aqui como
 * hacerlo"*).
 *
 * Va DENTRO de la pagina y no como un archivo aparte: se lee en el telefono,
 * esta en su idioma, y no publica material de otra compañia en una pagina que
 * lleva el logo de la agencia.
 */
function GuiaIdMe({ lang, t }) {
  const [abierto, setAbierto] = useState(true);
  // Si la lámina no carga (conexión mala, archivo movido), se quita y quedan
  // los pasos escritos. Un icono de imagen rota en la cara del cliente es peor
  // que no enseñar la foto.
  const [falloDeLamina, setFalloDeLamina] = useState(false);
  return (
    <div style={{ padding: ".9rem 1rem", background: "#f8fbff", borderTop: "1px solid #eef2f7" }}>
      <button
        onClick={() => setAbierto((v) => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 8, width: "100%",
          background: "none", border: "none", padding: 0, cursor: "pointer",
          font: "inherit", fontWeight: 800, fontSize: ".88rem", color: "#0a1628", textAlign: "left",
        }}
      >
        <BookOpen size={16} color={BRAND} />
        {t("Cómo crear tu cuenta ID.me", "How to create your ID.me account")}
        <span style={{ marginLeft: "auto", color: "#5b7a9a", fontWeight: 600 }}>{abierto ? "▾" : "▸"}</span>
      </button>

      {abierto && (
        <div style={{ marginTop: ".7rem" }}>
          {/* La lámina de la agencia, tal cual, lo PRIMERO que se ve
              (Lissette 2026-09-09). Se abre en grande al tocarla: la letra de
              los recuadros no se lee en un teléfono sin ampliar.
              Es un <a> y no un onClick para que "abrir en otra pestaña" y
              "guardar imagen" funcionen como en cualquier foto. */}
          {!falloDeLamina && (
            <a
              href={assetUrl(laminaDeIdMe(lang))}
              target="_blank"
              rel="noopener noreferrer"
              title={t("Tocar para verla en grande", "Tap to see it full size")}
              style={{ display: "block", marginBottom: ".85rem" }}
            >
              <img
                src={assetUrl(laminaDeIdMe(lang))}
                alt={t("Guía rápida ID.me: cómo crear tu cuenta, paso a paso",
                       "ID.me quick guide: how to create your account, step by step")}
                loading="lazy"
                onError={() => setFalloDeLamina(true)}
                style={{
                  width: "100%", height: "auto", display: "block",
                  borderRadius: 10, border: "1px solid #e1e8f0", background: "#fff",
                }}
              />
              {/* Un `title` no se ve en un teléfono, y ahí la lámina entra a
                  unos 340 px: la letra de los recuadros NO se lee sin ampliar.
                  Sin este pie, el cliente ve una foto borrosa y pasa de largo
                  sin saber que se abre. */}
              <span style={{
                display: "block", marginTop: 5, textAlign: "center",
                fontSize: ".76rem", color: BRAND, fontWeight: 700,
              }}>
                {t("👆 Tócala para verla en grande", "👆 Tap to see it full size")}
              </span>
            </a>
          )}
          <ol style={{ margin: 0, paddingLeft: "1.15rem", color: "#33506e", fontSize: ".85rem", lineHeight: 1.55 }}>
            {PASOS_ID_ME.map((paso, i) => <li key={i} style={{ marginBottom: 5 }}>{enIdioma(paso, lang)}</li>)}
          </ol>

          <div style={{ marginTop: ".8rem", fontSize: ".82rem", color: "#5b7a9a" }}>
            <strong style={{ color: "#33506e" }}>{t("Para que salga a la primera:", "To get it right the first time:")}</strong>
            <ul style={{ margin: "4px 0 0", paddingLeft: "1.15rem" }}>
              {CONSEJOS_ID_ME.map((c, i) => <li key={i}>{enIdioma(c, lang)}</li>)}
            </ul>
          </div>

          <a
            href={ENLACE_OFICIAL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-block", marginTop: ".85rem", padding: ".5rem .9rem",
              borderRadius: 9, background: BRAND, color: "#06283a",
              fontWeight: 800, fontSize: ".84rem", textDecoration: "none",
            }}
          >
            {t("Ir a ID.me", "Go to ID.me")}
          </a>

          {/* El ofrecimiento de ayuda. Dice que le acompañamos, que la cuenta
              es suya, y que nunca le pediremos la contraseña ni los codigos.
              Ese orden es el que evita que "te ayudamos con tu cuenta" se lea
              como "danos acceso a tu cuenta". */}
          <div style={{
            marginTop: ".9rem", padding: ".7rem .85rem", borderRadius: 10,
            background: "#fff", border: "1px solid #e1e8f0",
            fontSize: ".82rem", color: "#33506e", lineHeight: 1.5,
            display: "flex", gap: 8, alignItems: "flex-start",
          }}>
            <ShieldCheck size={16} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{enIdioma(AVISO_DE_AYUDA, lang)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ChoiceBtn({ active, color, icon, label, onClick }) {
  return (
    <button onClick={onClick} style={{
      flex: "1 1 160px", padding: "1rem .75rem", borderRadius: 12,
      border: `2px solid ${active ? color : "#e1e8f0"}`,
      background: active ? `${color}14` : "#fff",
      color: active ? color : "#3a5a7a", fontWeight: 800, fontSize: ".95rem",
      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      transition: "all .12s",
    }}>
      {icon} {label}
    </button>
  );
}

// Botón Sí/No de una pregunta. Más pequeño que ChoiceBtn a propósito: la
// decisión importante de la página sigue siendo "todo correcto / necesito un
// ajuste", y estos no deben competir con ella.
function YesNoBtn({ active, color, label, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      minWidth: 62, padding: ".45rem .9rem", borderRadius: 999,
      border: `2px solid ${active ? color : "#e1e8f0"}`,
      background: active ? `${color}14` : "#fff",
      color: active ? color : "#3a5a7a", fontWeight: 800, fontSize: ".85rem",
      cursor: "pointer", transition: "all .12s",
    }}>
      {label}
    </button>
  );
}

function SuccessCard({ t, choice, agentName }) {
  const correct = choice === "correct";
  return (
    <div style={{
      background: "#fff", border: "1px solid #e1e8f0", borderRadius: 16,
      padding: "2.5rem 1.5rem", textAlign: "center", marginTop: "1.5rem",
      boxShadow: "0 4px 20px rgba(0,0,0,.06)",
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: "50%", margin: "0 auto 1rem",
        background: correct ? "#dcfce7" : "#ffedd5",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {correct ? <CheckCircle size={34} color="#16a34a" /> : <Pencil size={30} color="#ea580c" />}
      </div>
      <h2 style={{ margin: "0 0 .5rem", fontSize: "1.3rem" }}>{t("¡Gracias!", "Thank you!")}</h2>
      <p style={{ color: "#5b7a9a", fontSize: ".93rem", lineHeight: 1.55, margin: 0 }}>
        {correct
          ? t(`Le confirmamos a ${agentName} que tus datos están correctos.`,
               `We've let ${agentName} know your information is correct.`)
          : t(`${agentName} recibió tu solicitud de ajuste y te contactará pronto.`,
               `${agentName} received your change request and will contact you soon.`)}
      </p>
    </div>
  );
}

